import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetActivePaymentMethods,
  GetActivePaymentStatuses,
  GetActiveReturnTypes,
  GetActiveOrderStatuses,
  GetActiveRefundType,
  GetActiveChequeStatuses,
  GetActiveUnits,
  GetActiveAttendanceStatuses,
} from "../Api";
import { ServiceQueryOptions, unwrapApiResponse } from "./query_helpers";
import { LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const lookupQueryKeys = {
  activePaymentMethods: () => ["activePaymentMethods"] as const,
  activePaymentStatuses: () => ["activePaymentStatuses"] as const,
  activeReturnTypes: () => ["activeReturnTypes"] as const,
  activeOrderStatuses: () => ["activeOrderStatuses"] as const,
  activeRefundType: () => ["activeRefundType"] as const,
  activeChequeStatuses: () => ["activeChequeStatuses"] as const,
  activeUnits: () => ["activeUnits"] as const,
  activeAttendanceStatuses: () => ["activeAttendanceStatuses"] as const,
};

export const invalidateLookupQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["activePaymentMethods"] });
  queryClient.invalidateQueries({ queryKey: ["activePaymentStatuses"] });
  queryClient.invalidateQueries({ queryKey: ["activeReturnTypes"] });
  queryClient.invalidateQueries({ queryKey: ["activeOrderStatuses"] });
  queryClient.invalidateQueries({ queryKey: ["activeRefundType"] });
  queryClient.invalidateQueries({ queryKey: ["activeChequeStatuses"] });
  queryClient.invalidateQueries({ queryKey: ["activeUnits"] });
  queryClient.invalidateQueries({ queryKey: ["activeAttendanceStatuses"] });
};

export const useActivePaymentMethodsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activePaymentMethods(),
    queryFn: async () => unwrapApiResponse(await GetActivePaymentMethods()),
  });
};

export const useActivePaymentMethodsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activePaymentMethods(),
        queryFn: async () => unwrapApiResponse(await GetActivePaymentMethods()),
      }),
  });
};

export const useActivePaymentStatusesQuery = (
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activePaymentStatuses(),
    queryFn: async () => unwrapApiResponse(await GetActivePaymentStatuses()),
  });
};

export const useActivePaymentStatusesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activePaymentStatuses(),
        queryFn: async () =>
          unwrapApiResponse(await GetActivePaymentStatuses()),
      }),
  });
};

export const useActiveReturnTypesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activeReturnTypes(),
    queryFn: async () => unwrapApiResponse(await GetActiveReturnTypes()),
  });
};

export const useActiveReturnTypesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activeReturnTypes(),
        queryFn: async () => unwrapApiResponse(await GetActiveReturnTypes()),
      }),
  });
};

export const useActiveOrderStatusesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activeOrderStatuses(),
    queryFn: async () => unwrapApiResponse(await GetActiveOrderStatuses()),
  });
};

export const useActiveOrderStatusesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activeOrderStatuses(),
        queryFn: async () => unwrapApiResponse(await GetActiveOrderStatuses()),
      }),
  });
};

export const useActiveRefundTypeQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activeRefundType(),
    queryFn: async () => unwrapApiResponse(await GetActiveRefundType()),
  });
};

export const useActiveRefundTypeRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activeRefundType(),
        queryFn: async () => unwrapApiResponse(await GetActiveRefundType()),
      }),
  });
};

export const useActiveChequeStatusesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activeChequeStatuses(),
    queryFn: async () => unwrapApiResponse(await GetActiveChequeStatuses()),
  });
};

export const useActiveChequeStatusesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activeChequeStatuses(),
        queryFn: async () => unwrapApiResponse(await GetActiveChequeStatuses()),
      }),
  });
};

export const useActiveUnitsQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activeUnits(),
    queryFn: async () => unwrapApiResponse(await GetActiveUnits()),
  });
};

export const useActiveUnitsRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activeUnits(),
        queryFn: async () => unwrapApiResponse(await GetActiveUnits()),
      }),
  });
};

export const useActiveAttendanceStatusesQuery = (options?: ServiceQueryOptions) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: lookupQueryKeys.activeAttendanceStatuses(),
    queryFn: async () => unwrapApiResponse(await GetActiveAttendanceStatuses()),
  });
};

export const useActiveAttendanceStatusesRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: lookupQueryKeys.activeAttendanceStatuses(),
        queryFn: async () => unwrapApiResponse(await GetActiveAttendanceStatuses()),
      }),
  });
};

export const useLookupService = (config?: {
  fetchActivePaymentMethodsEnabled?: boolean;
  fetchActivePaymentStatusesEnabled?: boolean;
  fetchActiveReturnTypesEnabled?: boolean;
  fetchActiveOrderStatusesEnabled?: boolean;
  fetchActiveRefundTypeEnabled?: boolean;
  fetchActiveChequeStatusesEnabled?: boolean;
  fetchActiveUnitsEnabled?: boolean;
  fetchActiveAttendanceStatusesEnabled?: boolean;
}) => {
  const activePaymentMethodsQuery = useActivePaymentMethodsQuery({
    enabled: config?.fetchActivePaymentMethodsEnabled !== false,
  });

  const activePaymentStatusesQuery = useActivePaymentStatusesQuery({
    enabled: config?.fetchActivePaymentStatusesEnabled !== false,
  });

  const activeReturnTypesQuery = useActiveReturnTypesQuery({
    enabled: config?.fetchActiveReturnTypesEnabled !== false,
  });

  const activeOrderStatusesQuery = useActiveOrderStatusesQuery({
    enabled: config?.fetchActiveOrderStatusesEnabled !== false,
  });

  const activeRefundTypeQuery = useActiveRefundTypeQuery({
    enabled: config?.fetchActiveRefundTypeEnabled !== false,
  });

  const activeChequeStatusesQuery = useActiveChequeStatusesQuery({
    enabled: config?.fetchActiveChequeStatusesEnabled !== false,
  });

  const activeUnitsQuery = useActiveUnitsQuery({
    enabled: config?.fetchActiveUnitsEnabled !== false,
  });

  const activeAttendanceStatusesQuery = useActiveAttendanceStatusesQuery({
    enabled: config?.fetchActiveAttendanceStatusesEnabled !== false,
  });

  return {
    activePaymentMethodsQuery,
    activePaymentStatusesQuery,
    activeReturnTypesQuery,
    activeOrderStatusesQuery,
    activeRefundTypeQuery,
    activeChequeStatusesQuery,
    activeUnitsQuery,
    activeAttendanceStatusesQuery,
  };
};
